import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  useColorModeValue,
  Icon,
  Image,
  Badge,
  useToast,
  SimpleGrid,
  Collapse,
} from '@chakra-ui/react';
import {
  FaSearch,
  FaGift,
  FaCheck,
  FaHeart,
  FaChevronDown,
  FaChevronRight,
} from 'react-icons/fa';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  indicateCardAvailable,
  indicateCardWanted,
  removeScambio,
  removeContatto,
} from '../../utils/scambiApi';
import MatchIndicator from './MatchIndicator';
import MatchDetailsModal from './MatchDetailsModal';
import EditScambioModal from './EditScambioModal';

export default function ScambiList({
  scambi,
  activeTab,
  userData,
  onCardUpdated,
}) {
  const toast = useToast();
  const [processingMatches, setProcessingMatches] = useState(new Set());
  const [selectedScambio, setSelectedScambio] = useState(null);
  const [isMatchModalOpen, setIsMatchModalOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState([]);
  const [editModal, setEditModal] = useState({
    isOpen: false,
    scambio: null,
  });
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    scambio: null,
  });

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardShadow = useColorModeValue('lg', 'xl');

  // CSS per l'animazione pulse
  const pulseKeyframes = `
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
  `;

  // Inserisci gli stili CSS
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = pulseKeyframes;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const getTypeIcon = (tipo) => {
    if (tipo === 'cerco') return FaSearch;
    if (tipo === 'scambio') return FaGift;
    return FaSearch;
  };

  const getTypeColor = (tipo) => {
    if (tipo === 'cerco') return 'green';
    if (tipo === 'scambio') return 'purple';
    return 'blue';
  };

  const getTypeLabel = (tipo) => {
    if (tipo === 'cerco') return 'Cerco';
    if (tipo === 'scambio') return 'Scambio';
    return tipo;
  };

  const getStatoColor = (stato) => {
    if (stato === 'attivo') return 'green';
    if (stato === 'in-trattativa') return 'yellow';
    if (stato === 'completato') return 'blue';
    if (stato === 'annullato') return 'red';
    return 'gray';
  };

  const getStatoLabel = (stato) => {
    if (stato === 'attivo') return '🟢 Attivo';
    if (stato === 'in-trattativa') return '🟡 In Trattativa';
    if (stato === 'completato') return '✅ Completato';
    if (stato === 'annullato') return '❌ Annullato';
    return '❓ Sconosciuto';
  };

  const handleCeLho = async (scambio) => {
    if (!userData) {
      toast({
        title: 'Accesso richiesto',
        description: 'Devi essere loggato per indicare di avere una carta',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setProcessingMatches((prev) => new Set(prev).add(scambio.documentId));

    try {
      await indicateCardAvailable(scambio.documentId);
      toast({
        title: 'Perfetto!',
        description: 'Sei stato aggiunto ai contatti per questo scambio',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      if (onCardUpdated) onCardUpdated();
    } catch (error) {
      toast({
        title: 'Errore',
        description: error.message || 'Impossibile aggiungere il contatto',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setProcessingMatches((prev) => {
        const newSet = new Set(prev);
        newSet.delete(scambio.documentId);
        return newSet;
      });
    }
  };

  const handleMiServe = async (scambio) => {
    if (!userData) {
      toast({
        title: 'Accesso richiesto',
        description: 'Devi essere loggato per indicare interesse in una carta',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setProcessingMatches((prev) => new Set(prev).add(scambio.documentId));

    try {
      await indicateCardWanted(scambio.documentId);
      toast({
        title: 'Interesse registrato!',
        description: 'Sei stato aggiunto ai contatti per questo scambio',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      if (onCardUpdated) onCardUpdated();
    } catch (error) {
      toast({
        title: 'Errore',
        description: error.message || 'Impossibile aggiungere il contatto',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setProcessingMatches((prev) => {
        const newSet = new Set(prev);
        newSet.delete(scambio.documentId);
        return newSet;
      });
    }
  };

  const handleRemoveContatto = async (scambio) => {
    if (!userData) return;

    setProcessingMatches((prev) => new Set(prev).add(scambio.documentId));

    try {
      await removeContatto(scambio.documentId);
      toast({
        title: 'Contatto rimosso!',
        description: 'Sei stato rimosso dai contatti per questo scambio',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      if (onCardUpdated) onCardUpdated();
    } catch (error) {
      toast({
        title: 'Errore',
        description: error.message || 'Impossibile rimuovere il contatto',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setProcessingMatches((prev) => {
        const newSet = new Set(prev);
        newSet.delete(scambio.documentId);
        return newSet;
      });
    }
  };

  const handleMatchIndicatorClick = (scambio) => {
    setSelectedScambio(scambio);
    setIsMatchModalOpen(true);
  };

  const handleEditScambio = (scambio) => {
    setEditModal({
      isOpen: true,
      scambio,
    });
  };

  const handleDeleteScambio = (scambio) => {
    setDeleteModal({
      isOpen: true,
      scambio,
    });
  };

  const confirmDeleteScambio = async () => {
    const { scambio } = deleteModal;

    try {
      await removeScambio(scambio.documentId);
      toast({
        title: 'Scambio eliminato!',
        description: 'Lo scambio è stato rimosso con successo',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      if (onCardUpdated) onCardUpdated();
      setDeleteModal({ isOpen: false, scambio: null });
    } catch (error) {
      toast({
        title: 'Errore',
        description: error.message || 'Impossibile eliminare lo scambio',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleScambioUpdated = () => {
    toast({
      title: 'Scambio aggiornato!',
      description:
        'I dettagli dello scambio sono stati modificati con successo',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    if (onCardUpdated) onCardUpdated();
    setEditModal({ isOpen: false, scambio: null });
  };

  const isProcessing = (scambioId) => processingMatches.has(scambioId);

  const canUpdateStatus = (scambio) => {
    if (!userData) return false;

    // Controlliamo tutti i possibili campi per l'ID utente
    const ownerId =
      scambio.user?.data?.id ||
      scambio.user?.id ||
      scambio.userId ||
      scambio.owner ||
      scambio.createdBy;

    return userData.id === ownerId;
  };

  const isUserInContatti = (scambio, tipoContatto = null) => {
    if (!userData || !scambio.tags || !Array.isArray(scambio.tags))
      return false;

    // Se stiamo cercando un tipo specifico di contatto
    if (tipoContatto) {
      return scambio.tags.some((tag) => {
        if (tag.startsWith(`${tipoContatto}_`)) {
          const parts = tag.split('_');
          if (parts.length >= 4) {
            const firstName = parts[1];
            const lastName = parts[2];
            return (
              firstName === userData.firstName && lastName === userData.lastName
            );
          }
        }
        return false;
      });
    }

    // Altrimenti controlliamo se l'utente è presente in qualsiasi tag
    return scambio.tags.some((tag) => {
      if (tag.startsWith('ce-lho_') || tag.startsWith('mi-serve_')) {
        const parts = tag.split('_');
        if (parts.length >= 4) {
          const firstName = parts[1];
          const lastName = parts[2];
          return (
            firstName === userData.firstName && lastName === userData.lastName
          );
        }
      }
      return false;
    });
  };

  if (scambi.length === 0) {
    return (
      <Box
        bg={cardBg}
        boxShadow={cardShadow}
        borderRadius="lg"
        p={8}
        textAlign="center"
      >
        <VStack spacing={4}>
          <Icon as={FaSearch} boxSize={12} color="gray.400" />
          <Text fontSize="lg" color="gray.600">
            {activeTab === 'tutti' && 'Nessuno scambio disponibile al momento'}
            {activeTab === 'cerco' && 'Nessuna carta richiesta al momento'}
            {activeTab === 'scambio' && 'Nessuna carta in scambio al momento'}
            {activeTab === 'attivo' && 'Nessuno scambio attivo al momento'}
            {activeTab === 'completato' &&
              'Nessuno scambio completato al momento'}
            {activeTab === 'match' && 'Nessun match attivo al momento'}
          </Text>
          <Text color="gray.500">
            {activeTab === 'match'
              ? 'Inizia a indicare le tue carte disponibili o i tuoi interessi!'
              : 'Sii il primo ad aggiungere una carta!'}
          </Text>
        </VStack>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Scambi List */}
      <SimpleGrid
        columns={{ base: 1, sm: 2, md: 3 }}
        spacing={6}
        align="stretch"
      >
        {scambi.map((scambio) => (
          <Box
            key={scambio.documentId}
            bg={cardBg}
            boxShadow={cardShadow}
            borderRadius="lg"
            p={6}
            borderLeft="4px solid"
            borderLeftColor={`${getTypeColor(scambio.tipo)}.500`}
            textAlign="center"
          >
            <VStack spacing={4} align="center" w="100%">
              {/* Card Image with Foil Badge */}
              <Box position="relative" display="inline-block">
                <Image
                  src={
                    scambio.carte?.image_uris?.normal ||
                    scambio.carte?.image_uris?.small ||
                    '/default-card-image.png'
                  }
                  alt={scambio.carte?.nome || 'Carta'}
                  borderRadius="lg"
                  boxSize={{ base: '240px', md: '300px' }}
                  objectFit="contain"
                  fallbackSrc="/default-card-image.png"
                  shadow="lg"
                  border="2px solid"
                  borderColor="gray.200"
                />
                {/* Foil Badge - positioned in top right corner */}
                {scambio.carte?.is_foil && (
                  <Badge
                    position="absolute"
                    top="2"
                    right="2"
                    size="sm"
                    colorScheme="yellow"
                    variant="solid"
                    zIndex="1"
                    boxShadow="md"
                  >
                    ✨ Foil
                  </Badge>
                )}
              </Box>

              {/* Card Info */}
              <VStack spacing={3} align="center" w="100%">
                {/* Badge e timestamp */}
                <HStack spacing={2} wrap="wrap" justify="center">
                  <Badge
                    colorScheme={getTypeColor(scambio.tipo)}
                    variant="solid"
                    fontSize="sm"
                    px={3}
                    py={1}
                  >
                    <Icon as={getTypeIcon(scambio.tipo)} mr={1} />
                    {getTypeLabel(scambio.tipo)}
                  </Badge>
                  <Badge
                    colorScheme={getStatoColor(scambio.stato)}
                    variant="outline"
                    fontSize="xs"
                    px={2}
                    py={1}
                  >
                    {getStatoLabel(scambio.stato)}
                  </Badge>
                </HStack>

                <Text fontSize="xs" color="gray.500" textAlign="center">
                  {formatDistanceToNow(new Date(scambio.createdAt), {
                    addSuffix: true,
                    locale: it,
                  })}
                </Text>

                {/* Nome carta */}
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  color="gray.800"
                  textAlign="center"
                  lineHeight="1.2"
                >
                  {scambio.carte?.nome || 'Carta senza nome'}
                </Text>

                {/* Note */}
                {scambio.note && (
                  <Text
                    color="gray.700"
                    fontSize="sm"
                    textAlign="center"
                    px={2}
                  >
                    {scambio.note}
                  </Text>
                )}

                {/* Informazioni aggiuntive dalla carta */}
                {scambio.carte && (
                  <VStack spacing={2} align="center">
                    {scambio.carte.set_name && (
                      <Badge size="sm" colorScheme="blue" variant="subtle">
                        {scambio.carte.set_name}
                      </Badge>
                    )}
                    {scambio.carte.lingua && scambio.carte.lingua !== 'en' && (
                      <Badge size="sm" colorScheme="orange" variant="subtle">
                        {scambio.carte.lingua === 'it'
                          ? 'Italiano'
                          : scambio.carte.lingua === 'de'
                            ? 'Tedesco'
                            : scambio.carte.lingua === 'fr'
                              ? 'Francese'
                              : scambio.carte.lingua === 'es'
                                ? 'Spagnolo'
                                : scambio.carte.lingua === 'pt'
                                  ? 'Portoghese'
                                  : scambio.carte.lingua === 'ru'
                                    ? 'Russo'
                                    : scambio.carte.lingua === 'ja'
                                      ? 'Giapponese'
                                      : scambio.carte.lingua === 'ko'
                                        ? 'Coreano'
                                        : scambio.carte.lingua === 'zh'
                                          ? 'Cinese'
                                          : scambio.carte.lingua}
                      </Badge>
                    )}
                    {scambio.carte.condizioni && (
                      <Badge size="sm" colorScheme="purple" variant="subtle">
                        {scambio.carte.condizioni === 'MT'
                          ? 'Mint'
                          : scambio.carte.condizioni === 'NM'
                            ? 'Near Mint'
                            : scambio.carte.condizioni === 'LP'
                              ? 'Lightly Played'
                              : scambio.carte.condizioni === 'MP'
                                ? 'Moderately Played'
                                : scambio.carte.condizioni === 'HP'
                                  ? 'Heavily Played'
                                  : scambio.carte.condizioni === 'DMG'
                                    ? 'Damaged'
                                    : scambio.carte.condizioni}
                      </Badge>
                    )}
                  </VStack>
                )}

                {/* Indicatori dei match */}
                <MatchIndicator
                  scambio={scambio}
                  userData={userData}
                  onClick={() => handleMatchIndicatorClick(scambio)}
                />
              </VStack>

              {/* Actions */}
              {/* Pulsanti per indicare disponibilità o interesse */}
              <VStack spacing={2} align="end">
                {/* Pulsanti di azione - visibili solo se NON siamo il creatore dello scambio */}
                {!canUpdateStatus(scambio) && (
                  <HStack spacing={2} mt={2}>
                    {/* Mostra "Ce L'ho!" solo se lo scambio è di tipo "cerco" */}
                    {scambio.tipo === 'cerco' && (
                      <Button
                        size="sm"
                        colorScheme={
                          isUserInContatti(scambio, 'ce-lho') ? 'red' : 'green'
                        }
                        variant="outline"
                        leftIcon={
                          <Icon
                            as={
                              isUserInContatti(scambio, 'ce-lho')
                                ? FaCheck
                                : FaCheck
                            }
                          />
                        }
                        onClick={() =>
                          isUserInContatti(scambio, 'ce-lho')
                            ? handleRemoveContatto(scambio)
                            : handleCeLho(scambio)
                        }
                        isLoading={isProcessing(scambio.documentId)}
                        loadingText="..."
                        _hover={{
                          bg: isUserInContatti(scambio, 'ce-lho')
                            ? 'red.50'
                            : 'green.50',
                          borderColor: isUserInContatti(scambio, 'ce-lho')
                            ? 'red.400'
                            : 'green.400',
                        }}
                      >
                        {isUserInContatti(scambio, 'ce-lho')
                          ? 'Rimuovi contatto'
                          : "Ce L'ho!"}
                      </Button>
                    )}

                    {/* Mostra "Mi serve!" solo se lo scambio è di tipo "scambio" */}
                    {scambio.tipo === 'scambio' && (
                      <Button
                        size="sm"
                        colorScheme={
                          isUserInContatti(scambio, 'mi-serve') ? 'red' : 'blue'
                        }
                        variant="outline"
                        leftIcon={
                          <Icon
                            as={
                              isUserInContatti(scambio, 'mi-serve')
                                ? FaHeart
                                : FaHeart
                            }
                          />
                        }
                        onClick={() =>
                          isUserInContatti(scambio, 'mi-serve')
                            ? handleRemoveContatto(scambio)
                            : handleMiServe(scambio)
                        }
                        isLoading={isProcessing(scambio.documentId)}
                        loadingText="..."
                        _hover={{
                          bg: isUserInContatti(scambio, 'mi-serve')
                            ? 'red.50'
                            : 'blue.50',
                          borderColor: isUserInContatti(scambio, 'mi-serve')
                            ? 'red.400'
                            : 'blue.400',
                        }}
                      >
                        {isUserInContatti(scambio, 'mi-serve')
                          ? 'Rimuovi contatto'
                          : 'Mi serve!'}
                      </Button>
                    )}
                  </HStack>
                )}

                {/* Sezione contatti - visibile solo al creatore dello scambio e solo se ci sono soci interessati */}
                {canUpdateStatus(scambio) &&
                  scambio.tags &&
                  scambio.tags.filter(
                    (tag) =>
                      tag.startsWith('ce-lho_') || tag.startsWith('mi-serve_')
                  ).length > 0 && (
                    <Box
                      mt={4}
                      p={3}
                      bg="gray.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.200"
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          // Toggle per questo specifico scambio
                          const scambioId = scambio.documentId;
                          if (openDropdowns.includes(scambioId)) {
                            setOpenDropdowns(
                              openDropdowns.filter((id) => id !== scambioId)
                            );
                          } else {
                            setOpenDropdowns([...openDropdowns, scambioId]);
                          }
                        }}
                        w="100%"
                        justifyContent="space-between"
                        _hover={{ bg: 'gray.100' }}
                      >
                        <Text fontWeight="bold" color="gray.700">
                          👥 Soci interessati (
                          {
                            scambio.tags.filter(
                              (tag) =>
                                tag.startsWith('ce-lho_') ||
                                tag.startsWith('mi-serve_')
                            ).length
                          }
                          )
                        </Text>
                        <Icon
                          as={
                            openDropdowns.includes(scambio.documentId)
                              ? FaChevronDown
                              : FaChevronRight
                          }
                          color="gray.500"
                          transition="transform 0.2s"
                        />
                      </Button>

                      <Collapse
                        in={openDropdowns.includes(scambio.documentId)}
                        animateOpacity
                      >
                        <Box mt={3}>
                          <VStack spacing={2} align="stretch">
                            {scambio.tags
                              .filter(
                                (tag) =>
                                  tag.startsWith('ce-lho_') ||
                                  tag.startsWith('mi-serve_')
                              )
                              .map((tag) => {
                                // Parsing del tag: ce-lho_firstName_lastName_telefono
                                const parts = tag.split('_');
                                if (parts.length >= 4) {
                                  const tipo = parts[0];
                                  const firstName = parts[1];
                                  const lastName = parts[2];
                                  const telefono = parts[3];

                                  // Creiamo una chiave univoca basata sui dati del tag
                                  const uniqueKey = `${tipo}_${firstName}_${lastName}_${telefono}`;

                                  return (
                                    <Box
                                      key={uniqueKey}
                                      p={3}
                                      bg="white"
                                      borderRadius="md"
                                      border="1px solid"
                                      borderColor="gray.200"
                                      shadow="sm"
                                    >
                                      <VStack align="start" spacing={1}>
                                        <Text
                                          fontWeight="semibold"
                                          fontSize="sm"
                                          color="gray.800"
                                        >
                                          {firstName} {lastName}
                                        </Text>

                                        {telefono && telefono !== 'no-tel' && (
                                          <Text fontSize="sm" color="gray.600">
                                            📱 {telefono}
                                          </Text>
                                        )}
                                      </VStack>
                                    </Box>
                                  );
                                }
                                return null;
                              })}
                          </VStack>
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                {/* Pulsanti per modificare lo scambio (solo per il creatore) */}
                {canUpdateStatus(scambio) && (
                  <HStack spacing={2} justify="center" w="100%">
                    <Button
                      size="sm"
                      colorScheme="blue"
                      variant="outline"
                      onClick={() => {
                        handleEditScambio(scambio);
                      }}
                      _hover={{
                        bg: 'blue.50',
                      }}
                    >
                      ✏️ Modifica
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="red"
                      variant="outline"
                      onClick={() => {
                        handleDeleteScambio(scambio);
                      }}
                      _hover={{
                        bg: 'red.50',
                      }}
                    >
                      🗑️ Elimina
                    </Button>
                  </HStack>
                )}
              </VStack>
            </VStack>
          </Box>
        ))}
      </SimpleGrid>

      {/* Match Details Modal */}
      <MatchDetailsModal
        isOpen={isMatchModalOpen}
        onClose={() => {
          setIsMatchModalOpen(false);
          setSelectedScambio(null);
        }}
        scambio={selectedScambio}
      />

      {/* Edit Scambio Modal */}
      <EditScambioModal
        isOpen={editModal.isOpen}
        onClose={() => setEditModal({ isOpen: false, scambio: null })}
        onScambioUpdated={handleScambioUpdated}
        scambio={editModal.scambio}
      />

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <Box
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="rgba(0, 0, 0, 0.5)"
          zIndex={1000}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Box
            bg={cardBg}
            boxShadow={cardShadow}
            borderRadius="lg"
            p={6}
            maxW="400px"
            w="90%"
          >
            <VStack spacing={4} align="stretch">
              <Text
                fontSize="lg"
                fontWeight="bold"
                textAlign="center"
                color="red.600"
              >
                Conferma eliminazione
              </Text>
              <Text textAlign="center" color="gray.600">
                Sei sicuro di voler eliminare questo scambio?
              </Text>
              <Box
                p={4}
                bg="red.50"
                borderRadius="md"
                border="1px solid"
                borderColor="red.200"
              >
                <HStack spacing={3}>
                  <Image
                    src={
                      deleteModal.scambio?.carte?.image_uris?.normal ||
                      deleteModal.scambio?.carte?.image_uris?.small ||
                      '/default-card-image.png'
                    }
                    alt={deleteModal.scambio?.carte?.nome || 'Carta'}
                    borderRadius="md"
                    boxSize="60px"
                    objectFit="cover"
                    fallbackSrc="/default-card-image.png"
                  />
                  <Box>
                    <Text fontWeight="bold" color="gray.800">
                      {deleteModal.scambio?.carte?.nome || 'Carta senza nome'}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {getTypeLabel(deleteModal.scambio?.tipo)} -{' '}
                      {getStatoLabel(deleteModal.scambio?.stato)}
                    </Text>
                  </Box>
                </HStack>
              </Box>
              <Text
                fontSize="sm"
                color="red.600"
                textAlign="center"
                fontStyle="italic"
              >
                Questa azione non può essere annullata
              </Text>
              <HStack spacing={3} justify="center">
                <Button
                  variant="outline"
                  onClick={() =>
                    setDeleteModal({ isOpen: false, scambio: null })
                  }
                >
                  Annulla
                </Button>
                <Button colorScheme="red" onClick={confirmDeleteScambio}>
                  Elimina Scambio
                </Button>
              </HStack>
            </VStack>
          </Box>
        </Box>
      )}
    </VStack>
  );
}
